/**
 * Upload/delete/move failure messages.
 *
 * Upload and delete have no discriminated domain-error envelope — every failure
 * is a generic HTTP error, so a single message per operation is sufficient.
 *
 * Move is the exception, and the reason this file now switches on something:
 * it answers with `{error: "<CODE>"}` and a caller is expected to tell a missing
 * source, an occupied destination and an invalid request apart from the code
 * alone rather than by reading a message (spec 059 FR-017).
 */
export function getUploadFailureMessage(fileName: string): string {
  return `"${fileName}" failed to upload. Please try again.`;
}

export function getDeleteFailureMessage(fileName: string): string {
  return `"${fileName}" failed to delete. Please try again.`;
}

/**
 * The failure codes the move endpoint documents
 * (`specs/059-workspace-file-move/contracts/error-codes.md`).
 */
export const MOVE_CONFLICT = 'FILE_MOVE_CONFLICT';

interface MoveFailureBody {
  error?: unknown;
  message?: unknown;
}

/** The `error` code of a failed move, when the body carries one. */
export function getMoveFailureCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  const { error } = data as MoveFailureBody;
  return typeof error === 'string' ? error : undefined;
}

/**
 * What to tell the reader when a move fails.
 *
 * The service's own message is preferred where it carries information this app
 * cannot reconstruct — the item ceiling names the actual limit, which is
 * configured server-side and would be a guess here.
 */
export function getMoveFailureMessage(name: string, data: unknown): string {
  const code = getMoveFailureCode(data);
  const message =
    typeof (data as MoveFailureBody | null)?.message === 'string'
      ? ((data as MoveFailureBody).message as string)
      : undefined;

  switch (code) {
    case MOVE_CONFLICT:
      return `Something called "${name}" is already there.`;
    case 'FILE_NOT_FOUND':
      return `"${name}" is no longer there.`;
    case 'MOVE_TOO_MANY_ITEMS':
      return message ?? `"${name}" holds too many items to move at once.`;
    case 'FILE_VALIDATION_ERROR':
      return `"${name}" cannot be moved there.`;
    default:
      return `"${name}" failed to move. Please try again.`;
  }
}
